import { Link } from 'react-router-dom';

const LAST_UPDATED = 'July 24, 2026';

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-2xl font-semibold text-dark-slate mb-3">{title}</h2>
    <div className="space-y-3 text-slate-gray leading-relaxed">{children}</div>
  </section>
);

const ChildSafety = () => {
  return (
    <div className="min-h-screen bg-frost-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-dark-slate">GradLink</Link>
          <nav className="flex gap-6 text-sm text-slate-gray">
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
            <Link to="/privacy" className="hover:text-dark-slate">Privacy</Link>
            <Link to="/terms" className="hover:text-dark-slate">Terms</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold text-dark-slate mb-2">Child Safety Standards</h1>
        <p className="text-sm text-slate-gray mb-10">Last updated: {LAST_UPDATED}</p>

        <p className="text-slate-gray leading-relaxed mb-10">
          GradLink is committed to preventing the exploitation of children on our
          platform. This page documents the standards we apply to protect children
          from child sexual abuse and exploitation (CSAE), including child sexual
          abuse material (CSAM), and how we comply with applicable child safety
          laws. This document is published in fulfillment of Google Play's
          child safety standards policy for social apps.
        </p>

        <Section id="adults-only" title="1. GradLink is for adults only">
          <p>
            GradLink is intended for users age 18 and older. Our{' '}
            <Link to="/terms" className="text-dark-slate underline">Terms of Service</Link>{' '}
            require every user to be at least 18. On Google Play, GradLink is
            configured with a target audience of 18 and over, and we ask Google Play
            to block users it has determined to be minors from downloading the app
            or making in-app purchases. We do not knowingly permit anyone under 18
            to create an account.
          </p>
          <p>
            If we become aware that a user is under 18, we terminate the account
            and delete the associated data. To report an underage user, contact
            us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            .
          </p>
        </Section>

        <Section id="prohibited" title="2. Prohibited content and conduct">
          <p>
            Our{' '}
            <Link to="/terms" className="text-dark-slate underline">Terms of Service</Link>{' '}
            prohibit content and conduct that endangers children, including but
            not limited to:
          </p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Child sexual abuse material (CSAM) of any kind.</li>
            <li>Content that sexualizes minors, or grooming behavior directed at minors.</li>
            <li>Sexually explicit content of any kind, including nudity involving any person.</li>
            <li>Solicitation of minors, or attempts to contact users known or believed to be minors for sexual, romantic, or exploitative purposes.</li>
            <li>Trafficking, exploitation, or endangerment of children.</li>
            <li>Impersonating a minor or misrepresenting age to interact with the community.</li>
          </ul>
          <p>
            Violations result in immediate content removal, account termination,
            and where required by law, referral to authorities.
          </p>
        </Section>

        <Section id="reporting" title="3. In-app reporting and blocking">
          <p>
            Every user has direct in-app tools to report and block other users
            and any content they encounter:
          </p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Any user profile can be blocked from within a chat or from that user's profile.</li>
            <li>Any post, comment, message, clip, listing, event, or profile can be reported for review.</li>
            <li>Reports flagged as involving a minor or CSAM are routed to our safety queue for immediate review.</li>
          </ul>
          <p>
            Reports are triaged as soon as they are received. Reports involving
            suspected CSAM or a minor are treated as the highest priority.
          </p>
        </Section>

        <Section id="moderation" title="4. Content moderation">
          <p>
            User-generated content on GradLink — posts, comments, direct messages,
            clips, listings, and profile content — is passed through automated
            moderation using Anthropic's Claude models before it is broadly
            visible. The moderation system is tuned to flag sexually explicit
            content, content that sexualizes or targets minors, and other
            violations of our{' '}
            <Link to="/terms" className="text-dark-slate underline">Terms of Service</Link>
            . Flagged content is queued for human review and removed if it
            violates our policies. Media uploaded to GradLink is stored in Google
            Firebase Storage, which applies Google's SafeSearch and CSAM
            detection on our behalf.
          </p>
        </Section>

        <Section id="csae-response" title="5. CSAE and CSAM response">
          <p>
            If we identify CSAM on GradLink — whether through automated
            detection, user reports, or law enforcement notice — we act
            immediately:
          </p>
          <ul className="list-disc list-outside pl-6 space-y-1">
            <li>Preserve the content and associated account data as evidence.</li>
            <li>Remove the content from public view.</li>
            <li>Terminate the responsible account.</li>
            <li>Report to the National Center for Missing &amp; Exploited Children (NCMEC) CyberTipline as required by U.S. federal law (18 U.S.C. § 2258A), and cooperate with law enforcement requests.</li>
            <li>Report to relevant regional and national authorities as required by applicable law.</li>
          </ul>
          <p>
            Google Firebase Storage, which hosts uploaded media, performs
            automated CSAM detection and reports confirmed matches to NCMEC on
            our behalf as part of its service.
          </p>
        </Section>

        <Section id="hiring" title="6. Personnel safeguards">
          <p>
            Access to user data, moderation queues, and support tools is limited
            to authorized personnel with a legitimate operational need. Employees
            and contractors with such access are bound by our data-handling
            policies and confidentiality obligations.
          </p>
        </Section>

        <Section id="review" title="7. Standards review">
          <p>
            We review these standards at least annually and update them when our
            product, moderation systems, or applicable law change. The
            "Last updated" date at the top of this page reflects the latest
            revision.
          </p>
        </Section>

        <Section id="contact" title="8. Contact for child safety concerns">
          <p>
            To report suspected child sexual abuse material, grooming, or any
            other child-safety concern involving GradLink, contact us at{' '}
            <a href="mailto:hello@gradlink.co" className="text-dark-slate underline">
              hello@gradlink.co
            </a>
            . We monitor this inbox for safety reports and prioritize response.
          </p>
          <p>
            In the United States, you can also report directly to the NCMEC
            CyberTipline at{' '}
            <a href="https://report.cybertip.org/" target="_blank" rel="noopener noreferrer" className="text-dark-slate underline">
              report.cybertip.org
            </a>
            {' '}or by calling 1-800-843-5678. If a child is in immediate danger,
            call 911.
          </p>
        </Section>

        <footer className="border-t border-slate-200 pt-6 mt-16 text-sm text-slate-gray flex flex-wrap gap-4 justify-between">
          <span>&copy; {new Date().getFullYear()} GradLink</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-dark-slate">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-dark-slate">Terms</Link>
            <Link to="/login" className="hover:text-dark-slate">Sign in</Link>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default ChildSafety;
